import getenv from 'getenv';
import ReCAPTCHA from 'react-google-recaptcha';
import Script from 'next/script';
import type { KyResponse } from 'ky';
import { Field, Form, Formik } from 'formik';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import Checkbox from '@tmh-website/ui/Checkbox';
import FormSection from '@tmh-website/ui/FormSection';
import { assertIsDefined, getCaptchaHlBasedOnLocale } from '@tmh-website/shared/helpers';
import { ErrorMessage, SuccessMessage } from '@tmh-website/ui/FormResultMessages';
import { extractReCaptchaErrors, handleReCaptchaError } from '@tmh-website/services/recaptcha.client.service';
import { FormComponentBaseProps } from '@tmh-website/components/blocks/Form';
import { FormElementThemeColor, StyledFormElements, formElementThemes } from '@tmh-website/ui/FormElements';
import { isKnowledgeCenterWebinarPath, isKnowledgeCenterWhitepaperPath } from '@tmh-website/services/page.service';
import { useTracifyCaptureConversion } from '@tmh-website/services/tracify';
import type { ConversionId } from '@tmh-website/services/tracify';
import type {
  HubspotFormId,
  HubspotParameters,
  HubspotPortalId,
  HubspotRegion,
} from '@tmh-website/services/form.service';

import * as Styled from './HubSpotFormRenderer.styles';
import { fillValuesWithHiddenFields, getConversionIdFromValues } from './HubSpotFormRenderer.helpers';
import { HubSpotOperatorToValidationFnMap } from './hubspot-form-renderer.operators.service';
import { HubspotTrackingEventType, useHubSpotTracking } from './hubspot-form-renderer.tracking.service';
import {
  FormValidationManager,
  convertValuesToHubSpotSubmitObject,
  formComponentsMap,
  getFormById,
  getInitialValues,
  hubspotFieldToComponentProps,
  submitForm,
  type SubmitFormProps,
  DEFAULT_HUBSPOT_REGION,
} from './hubspot-form-renderer.service';
import type {
  CanHaveHubspotRegion,
  HubSpotField,
  HubSpotFieldGroup,
  HubSpotFormEntity,
  HubSpotFormRendererValues,
  HubSpotProxyError,
} from './HubSpotFormRenderer.types';
import { HubSpotLegalConsentOptionsType, PostSubmitActionType } from './HubSpotFormRenderer.types';

declare global {
  // noinspection JSUnusedGlobalSymbols
  interface Window {
    hbspt: {
      forms: {
        create: (config: any) => any;
      };
    };
  }
}

const NEXT_PUBLIC_HUBSPOT_PORTAL_ID: string = getenv('NEXT_PUBLIC_HUBSPOT_PORTAL_ID', '25323067');
const NEXT_PUBLIC_HUBSPOT_REGION: string = getenv('NEXT_PUBLIC_HUBSPOT_REGION', 'eu1');

export interface HubSpotFormRendererProps extends FormComponentBaseProps {
  /**
   * Disable our API integration and use the usual HubSpot embed code instead.
   */
  useNativeHubSpotEmbed?: boolean;

  /**
   * Pass a fake response to the form renderer. Used for testing in Storybook.
   */
  fakeHubspotResponse?: HubSpotFormEntity;

  /**
   * Pass the fakeReCaptchaValue that allows to bypass the captcha validation. Used for testing in Storybook.
   */
  fakeReCaptchaValue?: string;

  currentPath: string;
  isStaticSuccessMessageVisible?: boolean;
}

const HubSpotFormRenderer = ({
  buttonHoverThemeColor,
  buttonThemeColor,
  currentPath,
  fakeHubspotResponse,
  fakeReCaptchaValue,
  formElementThemeColor,
  formParameters,
  locale,
  useNativeHubSpotEmbed,
  isStaticSuccessMessageVisible = true,
}: HubSpotFormRendererProps) => {
  const { t } = useTranslation();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const hubspotFormId: HubspotFormId = (formParameters as HubspotParameters).params.hubspotFormId;
  const hubspotPortalId: HubspotPortalId = (formParameters as HubspotParameters).params.hubspotPortalId;
  const hubspotRegion: HubspotRegion =
    (formParameters as HubspotParameters).params.hubspotRegion || DEFAULT_HUBSPOT_REGION;
  const shouldTrackConversions: boolean = (formParameters as HubspotParameters).params.shouldTrackConversions ?? false;

  const uniqueId = 'hubspot_' + hubspotFormId;
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [initialView, setInitialView] = useState(false);
  const { sendHubSpotEvent } = useHubSpotTracking(locale, currentPath);
  const { textOpacity } = useTheme();
  const captureConversion = useTracifyCaptureConversion();

  const { data: form, isLoading } = useQuery(
    ['hubspot-form', hubspotFormId],
    () => getFormById({ hubspotFormId, hubspotRegion }),
    {
      enabled: !fakeHubspotResponse,
      initialData: fakeHubspotResponse,
      staleTime: Infinity,
      cacheTime: Infinity,
      retry: false,
    }
  );

  const handlePostSubmit = (_data: KyResponse, variables: SubmitFormProps, _context: unknown) => {
    if (shouldTrackConversions) {
      const conversionId: ConversionId = getConversionIdFromValues(variables.values);
      void captureConversion(conversionId);
    }

    if (form?.configuration.postSubmitAction.type === PostSubmitActionType.Redirect) {
      window.location.href = form.configuration.postSubmitAction.value;
      return;
    }

    if (form?.configuration.postSubmitAction.type === PostSubmitActionType.Message) {
      setSuccessMessage(form.configuration.postSubmitAction.value);
      return;
    }
  };

  const {
    mutate: submitFormMutation,
    isLoading: isSubmitting,
    error,
  } = useMutation(submitForm, {
    onSuccess: handlePostSubmit,
    onError: (e: HubSpotProxyError) => handleReCaptchaError(e),
  });

  /**
   * Check if the script is loaded and if not, load it.
   * Embed the form when possible.
   */
  useEffect(() => {
    if (form && window.hbspt) {
      setTimeout(() => {
        window.hbspt.forms.create({
          formId: form?.id,
          portalId: hubspotPortalId || NEXT_PUBLIC_HUBSPOT_PORTAL_ID,
          region: hubspotRegion || NEXT_PUBLIC_HUBSPOT_REGION,
          target: `#${uniqueId}`,
        });
      }, 200);
    }
  }, [form, scriptLoaded]);

  /**
   * Send tracking events to HubSpot
   */
  useEffect(() => {
    if (!initialView && form?.id) {
      setInitialView(true);
      if ((window as any)._hsq) {
        (window as any)._hsq.push(['trackFormView', form.id]);
      } else {
        // No HubSpot tracking script found, send the event manually
        sendHubSpotEvent(form?.id, HubspotTrackingEventType.FormView);
      }
    }
  }, [form]);

  /**
   * We can't submit data when the form has reCaptcha enabled.
   * reCaptcha should be disabled but if not, we need to load the script as a fallback.
   */
  const showEmbedForm = useNativeHubSpotEmbed || form?.configuration.recaptchaEnabled;

  const submitButtonId =
    currentPath && isKnowledgeCenterWebinarPath(currentPath, locale)
      ? 'hubspot-watch-button'
      : currentPath && isKnowledgeCenterWhitepaperPath(currentPath, locale)
      ? 'hubspot-download-button'
      : 'hubspot-submit-button';

  /**
   * Handles the `onSubmit` event fired by the `<Formik>`.
   *
   * @param values - The form values/data collected by `<Formik>`.
   */
  const handleFormikSubmit = async (values: HubSpotFormRendererValues): Promise<void> => {
    assertIsDefined(form);

    const valuesWithHiddenFields: HubSpotFormRendererValues = fillValuesWithHiddenFields(values);

    const payload: SubmitFormProps = {
      form,
      hubspotFormId,
      hubspotRegion,
      values: valuesWithHiddenFields,
    };

    if (fakeHubspotResponse) {
      console.log(convertValuesToHubSpotSubmitObject(payload));
    } else {
      submitFormMutation(payload);
    }
  };

  return (
    <div>
      <Script
        type='text/javascript'
        src={`https://js-${hubspotRegion}.hsforms.net/forms/embed/v2.js`}
        onLoad={() => setScriptLoaded(true)}
      />
      <div id={uniqueId} style={{ display: showEmbedForm ? undefined : 'none' }} aria-hidden={!showEmbedForm} />
      {!showEmbedForm && (
        <>
          {form && !isLoading ? (
            <>
              <Formik<HubSpotFormRendererValues>
                initialValues={{ ...getInitialValues(form), gRecaptchaResponse: fakeReCaptchaValue }}
                onSubmit={handleFormikSubmit}
                validationSchema={new FormValidationManager(form).createYupSchema()}>
                {({ values, setFieldValue, touched, errors }) => (
                  <Form data-cy='hubspotForm'>
                    {/* Form fields */}
                    {form.fieldGroups.map((group: HubSpotFieldGroup) => {
                      const filteredFields = group.fields?.filter(field => !field.hidden);
                      if (!filteredFields || filteredFields.length === 0) {
                        return null;
                      }
                      return (
                        <FormSection
                          key={filteredFields.map(field => field.name).join('_')}
                          numberOfColumns={filteredFields.length}>
                          {filteredFields.map(field => (
                            <Fragment key={field.name + (field.dependentFields ? 'with-field' : '')}>
                              <TransformedPropsField
                                {...{ field, hubspotRegion }}
                                key={field.name}
                                themeColor={formElementThemeColor}
                              />

                              {field.dependentFields?.map(entry => {
                                const validationFn =
                                  HubSpotOperatorToValidationFnMap[entry.dependentCondition.operator];
                                const targetValue = entry.dependentCondition.value ?? entry.dependentCondition.values;
                                const parentValue = values[field.name];
                                const parentValueIsEmptyAndRequired =
                                  (!parentValue || parentValue.length === 0) && field.required;
                                if (parentValueIsEmptyAndRequired || !validationFn(parentValue, targetValue)) {
                                  return null;
                                }
                                return (
                                  <TransformedPropsField
                                    {...{ hubspotRegion }}
                                    key={entry.dependentField.name}
                                    field={entry.dependentField}
                                    themeColor={formElementThemeColor}
                                  />
                                );
                              })}
                            </Fragment>
                          ))}
                        </FormSection>
                      );
                    })}

                    {/* Legal consent options */}
                    {form.legalConsentOptions.type !== HubSpotLegalConsentOptionsType.None && (
                      <Styled.ConsentOptions>
                        {form.legalConsentOptions.communicationConsentText && (
                          <Styled.AdjustedRichtext
                            input={form.legalConsentOptions.communicationConsentText}
                            typographyProps={{
                              as: 'div',
                              $style: 'uiText',
                              $color: formElementThemes[formElementThemeColor].text,
                              $opacity: textOpacity,
                            }}
                          />
                        )}
                        {form.legalConsentOptions.communicationsCheckboxes && (
                          <Styled.CheckboxesContainer>
                            {form.legalConsentOptions.communicationsCheckboxes.map(checkbox => (
                              <Field
                                key={checkbox.subscriptionTypeId}
                                as={Checkbox}
                                name={'communication__' + checkbox.subscriptionTypeId}
                                required={checkbox.required}
                                themeColor={formElementThemeColor}>
                                <Styled.AdjustedRichtext
                                  input={checkbox.label}
                                  injectRequiredIndicator={checkbox.required}
                                />
                              </Field>
                            ))}
                          </Styled.CheckboxesContainer>
                        )}
                        {form.legalConsentOptions.consentToProcessText && (
                          <Styled.AdjustedRichtext
                            input={form.legalConsentOptions.consentToProcessText}
                            typographyProps={{
                              as: 'div',
                              $style: 'uiText',
                              $color: formElementThemes[formElementThemeColor].text,
                              $opacity: textOpacity,
                            }}
                          />
                        )}
                        {form.legalConsentOptions.consentToProcessCheckboxLabel && (
                          <Field as={Checkbox} name={'consentToProcess'} required themeColor={formElementThemeColor}>
                            <Styled.AdjustedRichtext
                              input={form.legalConsentOptions.consentToProcessCheckboxLabel}
                              injectRequiredIndicator
                            />
                          </Field>
                        )}
                        {form.legalConsentOptions.consentToProcessFooterText && (
                          <Styled.AdjustedRichtext
                            input={form.legalConsentOptions.consentToProcessFooterText}
                            typographyProps={{
                              as: 'div',
                              $style: 'uiText',
                              $color: formElementThemes[formElementThemeColor].text,
                              $opacity: textOpacity,
                            }}
                          />
                        )}
                        {form.legalConsentOptions.privacyText && (
                          <Styled.AdjustedRichtext
                            input={form.legalConsentOptions.privacyText}
                            typographyProps={{
                              as: 'div',
                              $style: 'uiText',
                              $color: formElementThemes[formElementThemeColor].text,
                              $opacity: textOpacity,
                            }}
                          />
                        )}
                      </Styled.ConsentOptions>
                    )}

                    <Styled.CaptchaContainer>
                      <ReCAPTCHA
                        hl={getCaptchaHlBasedOnLocale(locale)}
                        onChange={token => setFieldValue('gRecaptchaResponse', token)}
                        sitekey={getenv('NEXT_PUBLIC_RECAPTCHA_SITE_KEY', process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY)}
                        size='normal'
                      />
                    </Styled.CaptchaContainer>

                    {/* Submit button */}
                    <Styled.SubmitButton
                      id={submitButtonId}
                      type='submit'
                      themeColor={buttonThemeColor}
                      hoverThemeColor={buttonHoverThemeColor}
                      isLoading={isSubmitting}
                      disabled={
                        !!successMessage || Object.keys(touched).length === 0 || Object.keys(errors).length > 0
                      }>
                      {form.displayOptions.submitButtonText}
                    </Styled.SubmitButton>
                  </Form>
                )}
              </Formik>
              {successMessage && (
                <>
                  {isStaticSuccessMessageVisible && (
                    <SuccessMessage marginTop>{t('global:contact.successMessage')}</SuccessMessage>
                  )}
                  <Styled.HubSpotSuccessMessage dangerouslySetInnerHTML={{ __html: successMessage }} />
                </>
              )}
              {!!error && (
                <ErrorMessage marginTop>
                  {error.errorType === 'hubspot' || extractReCaptchaErrors(error, t).length === 0
                    ? (t('global:errorUnknown') as string)
                    : extractReCaptchaErrors(error, t).join('\n')}
                </ErrorMessage>
              )}
            </>
          ) : (
            <>
              <FormSection numberOfColumns={1}>
                <StyledFormElements.FormElementSkeleton $themeColor={formElementThemeColor} />
              </FormSection>
              <FormSection numberOfColumns={2}>
                <StyledFormElements.FormElementSkeleton $themeColor={formElementThemeColor} />
                <StyledFormElements.FormElementSkeleton $themeColor={formElementThemeColor} />
              </FormSection>
              <FormSection numberOfColumns={1}>
                <StyledFormElements.FormElementSkeleton $themeColor={formElementThemeColor} />
                <StyledFormElements.FormElementSkeleton $themeColor={formElementThemeColor} />
              </FormSection>
              <FormSection numberOfColumns={1}>
                <StyledFormElements.FormElementSkeleton $themeColor={formElementThemeColor} />
                <StyledFormElements.FormElementSkeleton $themeColor={formElementThemeColor} />
              </FormSection>
            </>
          )}
        </>
      )}
    </div>
  );
};

const TransformedPropsField = ({
  field,
  hubspotRegion = DEFAULT_HUBSPOT_REGION,
  themeColor,
}: {
  field: HubSpotField;
  themeColor: FormElementThemeColor;
} & CanHaveHubspotRegion) => {
  const transformedProps = useMemo(
    () => hubspotFieldToComponentProps(field, themeColor, hubspotRegion),
    [field, themeColor]
  );

  const children = useMemo(() => {
    if (transformedProps && 'children' in transformedProps) {
      return transformedProps.children;
    }
  }, [transformedProps]);

  return (
    <Field as={formComponentsMap[field.fieldType]} key={field.name} {...transformedProps}>
      {children}
    </Field>
  );
};

export default HubSpotFormRenderer;
